import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { servicesUrlConfig } from "shared/config/setting";
import {
  getAuthenticatedUserInfo,
  loginForAccessToken,
} from "shared/services/auth-services/auth_service";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

import axios from "axios";

const AUTH_API = servicesUrlConfig.AUTH_API;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [auth, setAuth] = useState({ token: "", isAuth: false });
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const history = useHistory();
  const location = useLocation();

  // Helper: Save tokens to localStorage
  const persistTokens = (at, rt) => {
    setAccessToken(at);
    setRefreshToken(rt);
    if (at && rt) {
      localStorage.setItem("accessToken", at);
      localStorage.setItem("refreshToken", rt);
    } else {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
    }
  };

  // On mount: restore tokens and user
  useEffect(() => {
    const at = localStorage.getItem("accessToken");
    const rt = localStorage.getItem("refreshToken");
    if (at && rt) {
      setAccessToken(at);
      setRefreshToken(rt);
      fetchUser(at)
        .catch(() => {
          // Possibly expired accessToken, try rotate
          refreshTokens(rt).catch(() => {
            persistTokens(null, null);
            setUser(null);
            setAuth({ token: "", isAuth: false });
          });
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  // Token Rotation
  const refreshTokens = useCallback(
    async (rt = refreshToken) => {
      if (!rt) throw new Error(t("notifications.sessionExpired"));
      // console.log("Request refresh token: url=", AUTH_API.refresh);
      const res = await fetch(AUTH_API.refresh, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) throw new Error(t("notifications.refreshTokenFailed"));
      const data = await res.json();
      persistTokens(data.accessToken, data.refreshToken);
      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      await fetchUser(data.accessToken);
    },
    [refreshToken, t],
  );

  // Fetch logged-in user info
  const fetchUser = async (token = accessToken) => {
    const userInfo = await getAuthenticatedUserInfo(token);
    if (!userInfo) throw new Error(t("notifications.userFetchFailed"));
    const data = userInfo;
    // console.log("Fetched user info:", data);
    setUser(data);
    setAuth({ token, isAuth: true, user: data });
    return data;
  };

  // Authenticated requests helper (handles rotation)
  const authFetch = useCallback(
    async (url, options = {}) => {
      let token = accessToken;
      // console.log("Fetch with token to url=", url, "options=", options);
      try {
        const response = await axios({
          url,
          method: options.method || "get",
          headers: {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`,
          },
          data: options.body, // pass body if POST/PUT
          params: options.params, // allow query params
          ...(options.responseType && { responseType: options.responseType }), // only include if provided
        });
        return response;
      } catch (error) {
        if (error.response && error.response.status === 401 && refreshToken) {
          try {
            await refreshTokens();
            token = localStorage.getItem("accessToken");
            // console.log("Retry axios request after refreshed token. url=", url);

            const retryResponse = await axios({
              url,
              method: options.method || "get",
              headers: {
                ...(options.headers || {}),
                Authorization: `Bearer ${token}`,
              },
              data: options.body,
              params: options.params,
              ...(options.responseType && {
                responseType: options.responseType,
              }), // only include if provided
            });
            return retryResponse;
          } catch (e) {
            logout();
            throw new Error(t("notifications.sessionExpired"));
          }
        } else {
          throw error; // rethrow other errors
        }
      }
    },
    [accessToken, refreshToken, refreshTokens, t],
  );

  // Login
  const login = async (e, username, password) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await loginForAccessToken(username, password);
      if (!data) {
        setLoading(false);
        throw new Error(t("notifications.loginError"));
      }
      persistTokens(data.access_token, data.refresh_token);
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      await fetchUser(data.access_token);
      setLoading(false);
      history.replace(location?.state?.from.pathname || "/");
    } catch (err) {
      setLoading(false);
      setError(err.message || t("notifications.genericError"));
      console.error("Login error:", err);
    }
  };

  // Logout
  const logout = async () => {
    try {
      if (accessToken) {
        await fetch(AUTH_API.logout, {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      }
    } catch {}
    setUser(null);
    setAuth({ token: "", isAuth: false });
    persistTokens(null, null);
  };

  const redirectToLoginPage = (fromLocation = "/") => {
    history.push({
      pathname: "/login",
      state: { from: location },
    });
  };

  // Authorization helpers
  const isAuthenticated = !!user;
  const isSuperUser = user?.is_superuser || false;
  const hasRole = (role) => user?.roles?.includes(role);
  const hasPermission = (perm) => user?.permissions?.includes(perm);

  // Expose context
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isSuperUser,
        auth,
        user,
        loading,
        error,
        setError,
        login,
        logout,
        hasRole,
        hasPermission,
        authFetch, // for authenticated API calls
        refreshTokens,
        redirectToLoginPage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// export function useAuth() {
//   const ctx = useContext(AuthContext);
//   if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
//   return ctx;
// }

export const useAuth = () => useContext(AuthContext);
